<ng-container *ngIf="isLoading; else gridContent">
  <app-loader />
</ng-container>

<ng-template #gridContent>
  <!-- Error state -->
  <div *ngIf="hasError" class="alert alert-danger m-3">
    <i class="fas fa-exclamation-triangle me-2"></i>{{ errorMessage }}
    <button class="btn btn-sm btn-outline-danger ms-3" (click)="loadSessions()">
      <i class="fas fa-redo me-1"></i>{{ 'BUTTONS.RETRY' | translate }}
    </button>
  </div>

  <div *ngIf="!hasError" class="p-1 p-sm-4 bg-dashboard-card br-9">

    <!-- Facettes de l'administration. Absentes de l'écran professeur : il ne
         voit que ses propres matières, trois listes à une seule valeur ne lui
         apprendraient rien. -->
    <div *ngIf="showFilters" class="ss-filters mb-3">
      <!-- L'unité du suivi est l'élève : le retrouver par son nom ne doit pas
           demander de feuilleter les pages. -->
      <div class="ss-filters__field">
        <label class="ss-filters__label" for="ss-student-search">{{ 'FOLLOWING.COL_STUDENT' | translate }}</label>
        <input id="ss-student-search" type="search" class="form-control form-control-sm"
               [(ngModel)]="studentSearch" (ngModelChange)="onFacetChange()"
               [placeholder]="'FILTERS.SEARCH_STUDENT_PLACEHOLDER' | translate" autocomplete="off">
      </div>

      <div class="ss-filters__field">
        <label class="ss-filters__label">{{ 'FOLLOWING.COL_LEARNING_PATH' | translate }}</label>
        <select class="form-select form-select-sm" [(ngModel)]="learningPathId"
                (ngModelChange)="onLearningPathChange()">
          <option [ngValue]="null">{{ 'FOLLOWING.FILTER_ALL' | translate }}</option>
          <option *ngFor="let o of learningPathOptions" [ngValue]="o.id">{{ o.label }}</option>
        </select>
      </div>

      <div class="ss-filters__field">
        <label class="ss-filters__label">{{ 'FOLLOWING.COL_SUBJECT' | translate }}</label>
        <select class="form-select form-select-sm" [(ngModel)]="subjectId"
                (ngModelChange)="onFacetChange()">
          <option [ngValue]="null">{{ 'FOLLOWING.FILTER_ALL' | translate }}</option>
          <option *ngFor="let o of subjectOptions" [ngValue]="o.id">{{ o.label }}</option>
        </select>
      </div>

      <div class="ss-filters__field">
        <label class="ss-filters__label">{{ 'FOLLOWING.COL_PROFESSOR' | translate }}</label>
        <select class="form-select form-select-sm" [(ngModel)]="professorId"
                (ngModelChange)="onFacetChange()">
          <option [ngValue]="null">{{ 'FOLLOWING.FILTER_ALL' | translate }}</option>
          <option *ngFor="let o of professorOptions" [ngValue]="o.id">{{ o.label }}</option>
        </select>
      </div>

      <button *ngIf="hasActiveFilter" type="button"
              class="btn btn-sm btn-outline-secondary ss-filters__reset"
              (click)="resetFilters()">
        <i class="fas fa-times me-1"></i>{{ 'FOLLOWING.FILTER_RESET' | translate }}
      </button>

      <span class="ss-filters__count">
        {{ 'FOLLOWING.FILTER_COUNT' | translate: { count: visibleData.length } }}
      </span>
    </div>

    <!-- Le suivi se lit par élève, pas par copie. Le rapport de diagnostic est
         une propriété de l'élève : répété sur chacune de ses tentatives, il
         devenait illisible dès qu'il en avait passé plusieurs, et laissait
         croire qu'il en existe un par copie. -->
    <div class="sst__students">
      @for (group of pagedStudentGroups; track group.studentId) {
        <div class="sst__student" [class.sst__student--open]="isExpanded(group)">
          <button type="button" class="sst__student-head" (click)="toggleStudent(group)"
                  [attr.aria-expanded]="isExpanded(group)">
            <i class="fas fa-chevron-right sst__chev" aria-hidden="true"></i>
            <i class="fas fa-user-circle text-primary" aria-hidden="true"></i>

            <span class="sst__student-id">
              <span class="sst__student-name">{{ group.fullName }}</span>
              <span class="sst__student-meta">
                {{ group.email }}
                @if (group.learningPathTitle) { · {{ group.learningPathTitle }} }
              </span>
            </span>

            <span class="sst__student-count">
              {{ 'FOLLOWING.SESSIONS_COUNT' | translate: { count: group.sessions.length } }}
            </span>
          </button>

          <!-- Proposé à qui peut l'ouvrir : l'API ne sert le rapport d'autrui
               qu'avec tests.read.all, et la vue professeur reste à écrire.
               La règle « visible mais inerte » vaut pour qui la détient. -->
          <div class="sst__student-actions" *appCan="'tests.read.all'">
            <!-- Sans diagnostic passé, l'action reste visible mais inerte, avec
                 sa raison : la masquer laisserait croire que le rapport
                 n'existe pas. -->
            <button type="button" class="sst__report"
                    [disabled]="group.diagnosticCount === 0"
                    (click)="openDiagnosticReport(group.studentId)">
              <i class="fas fa-file-waveform" aria-hidden="true"></i>
              @if (group.diagnosticCount > 0) {
                {{ 'FOLLOWING.DIAGNOSTIC_REPORT' | translate }}
              } @else {
                {{ 'FOLLOWING.NO_DIAGNOSTIC' | translate }}
              }
            </button>
          </div>

          @if (isExpanded(group)) {
            <!-- Trois regards sur le même élève : ce qu'il a rendu, le chemin
                 parcouru depuis son diagnostic, et où il en est par matière. -->
            <div class="sst__tabs" role="tablist">
              <button type="button" class="sst__tab" role="tab"
                      [class.sst__tab--on]="tabOf(group) === 'sessions'"
                      [attr.aria-selected]="tabOf(group) === 'sessions'"
                      (click)="selectTab(group, 'sessions')">
                {{ 'FOLLOWING.TAB_SESSIONS' | translate }}
              </button>
              <button type="button" class="sst__tab" role="tab"
                      [class.sst__tab--on]="tabOf(group) === 'progress'"
                      [attr.aria-selected]="tabOf(group) === 'progress'"
                      (click)="selectTab(group, 'progress')">
                {{ 'FOLLOWING.TAB_PROGRESS' | translate }}
              </button>
              <button type="button" class="sst__tab" role="tab"
                      [class.sst__tab--on]="tabOf(group) === 'subjects'"
                      [attr.aria-selected]="tabOf(group) === 'subjects'"
                      (click)="selectTab(group, 'subjects')">
                {{ 'FOLLOWING.TAB_SUBJECTS' | translate }}
              </button>
            </div>

            @switch (tabOf(group)) {
              @case ('sessions') {
                <div class="sst__sessions">
                  <app-custom-tab [data]="group.sessions" [columns]="columns"
                    [cellTemplates]="{
                      student: studentTpl, test: testTpl, subject: subjectTpl, status: statusTpl,
                      score: scoreTpl, passed: passedTpl, startedAt: startedTpl, antifraud: antifraudTpl
                    }"
                    [actionTemplate]="actionsTpl" [locale]="currentLocale" [enableRtl]="enableRTL">
                  </app-custom-tab>
                </div>
              }

              @case ('progress') {
                <div class="sst__pane">
                  @if (chartOf(group).length === 0) {
                    <p class="sst__empty">{{ 'FOLLOWING.NO_SCORE' | translate }}</p>
                  } @else {
                    <div class="sst__kpis">
                      <div class="sst__kpi">
                        <span class="sst__kpi-value">
                          @if (diagnosticScoreOf(group) !== null) {
                            {{ diagnosticScoreOf(group) | number: '1.0-0' }}%
                          } @else { — }
                        </span>
                        <span class="sst__kpi-label">{{ 'FOLLOWING.KPI_DIAGNOSTIC' | translate }}</span>
                      </div>
                      <div class="sst__kpi">
                        <span class="sst__kpi-value">
                          @if (progressOf(group) !== null) {
                            {{ progressOf(group)! > 0 ? '+' : '' }}{{ progressOf(group) }}
                          } @else { — }
                        </span>
                        <span class="sst__kpi-label">{{ 'FOLLOWING.KPI_SINCE' | translate }}</span>
                      </div>
                      <div class="sst__kpi">
                        <span class="sst__kpi-value">{{ averageOf(group) }}%</span>
                        <span class="sst__kpi-label">{{ 'FOLLOWING.KPI_AVERAGE' | translate }}</span>
                      </div>
                    </div>

                    <!-- L'axe va de 0 à 100 quoi qu'il arrive : le cadrer sur
                         les valeurs observées ferait paraître spectaculaire un
                         écart de deux points. -->
                    <svg class="sst__chart" viewBox="0 0 320 120"
                         role="img" [attr.aria-label]="'FOLLOWING.TAB_PROGRESS' | translate">
                      <line class="sst__grid" x1="22" y1="16" x2="298" y2="16"></line>
                      <line class="sst__grid" x1="22" y1="68" x2="298" y2="68"></line>
                      <line class="sst__grid" x1="22" y1="104" x2="298" y2="104"></line>
                      <text class="sst__axis" x="2" y="20">100</text>
                      <text class="sst__axis" x="6" y="72">50</text>
                      <text class="sst__axis" x="10" y="108">0</text>

                      <polyline class="sst__line" [attr.points]="polylineOf(group)"></polyline>

                      @for (p of chartOf(group); track p.when + p.title) {
                        <circle [attr.cx]="p.x" [attr.cy]="p.y" r="4"
                                [class]="p.diagnostic ? 'sst__dot sst__dot--start' : 'sst__dot'">
                          <title>{{ p.title }} — {{ p.score | number: '1.0-0' }}%</title>
                        </circle>
                      }
                    </svg>

                    <p class="sst__legend">{{ 'FOLLOWING.CHART_LEGEND' | translate }}</p>
                  }
                </div>
              }

              @case ('subjects') {
                <div class="sst__pane">
                  @if (isProgressionLoading(group)) {
                    <p class="sst__empty">{{ 'COMMON.LOADING' | translate }}</p>
                  } @else if (progressionOf(group).length === 0) {
                    <p class="sst__empty">{{ 'FOLLOWING.NO_PROGRESSION' | translate }}</p>
                  } @else {
                    <div class="sst__bars">
                      @for (s of progressionOf(group); track s.subjectId) {
                        <div class="sst__bar">
                          <span class="sst__bar-label">{{ s.subjectName }}</span>
                          <span class="sst__bar-value">
                            {{ s.completedTests }}/{{ s.totalTests }}
                            @if (s.averageScore != null) {
                              · {{ s.averageScore | number: '1.0-0' }}%
                            }
                          </span>
                          <span class="sst__bar-track">
                            <span class="sst__bar-fill"
                                  [style.width.%]="s.totalTests > 0 ? (s.completedTests / s.totalTests) * 100 : 0">
                            </span>
                          </span>
                        </div>
                      }
                    </div>

                    <!-- Le reste de l'écran ne montre que la période active ;
                         ce décompte-ci vient du serveur et couvre toute la
                         scolarité de l'élève. Sans le dire, on lit « 1/1 · 80 % »
                         en face d'une matière dont aucune copie n'apparaît. -->
                    <p class="sst__legend">{{ 'FOLLOWING.SUBJECTS_SCOPE' | translate }}</p>
                  }
                </div>
              }
            }
          }
        </div>
      } @empty {
        <p class="sst__empty">
          {{ (hasActiveFilter ? 'FILTERS.EMPTY_TITLE' : 'FOLLOWING.NO_SESSION') | translate }}
        </p>
      }
    </div>

    <!-- La pagination porte sur les élèves : c'est l'unité du suivi, et il y en
         a bien moins que de copies. -->
    @if (studentPageCount > 1) {
      <div class="sst__pager">
        <button type="button" class="btn btn-sm btn-outline-secondary"
                [disabled]="currentStudentPage === 0" (click)="goToStudentPage(currentStudentPage - 1)">
          ‹
        </button>
        <span class="sst__pager-label">
          {{ 'FOLLOWING.PAGE_OF' | translate: { page: currentStudentPage + 1, total: studentPageCount } }}
        </span>
        <button type="button" class="btn btn-sm btn-outline-secondary"
                [disabled]="currentStudentPage + 1 >= studentPageCount"
                (click)="goToStudentPage(currentStudentPage + 1)">
          ›
        </button>
      </div>
    }

    <ng-template #studentTpl let-data>
      <div class="d-flex align-items-center gap-2">
        <i class="fas fa-user-circle text-primary"></i>
        <div>
          <div class="fw-semibold small">{{ data.studentFullName || '—' }}</div>
          <div class="text-muted" style="font-size:11px">{{ data.studentEmail || '—' }}</div>
        </div>
      </div>
    </ng-template>

    <ng-template #testTpl let-data>
      <div>
        <div class="small fw-semibold">{{ data.testTitle || '—' }}</div>
        <span class="badge bg-light text-dark border" style="font-size:10px">{{ data.testMode || '—' }}</span>
      </div>
    </ng-template>

    <ng-template #subjectTpl let-data><span>{{ data.subjectName || '—' }}</span></ng-template>

    <ng-template #statusTpl let-data>
      <span [ngClass]="getStatusClass(data.status)">{{ data.status }}</span>
    </ng-template>

    <ng-template #scoreTpl let-data>
      <ng-container *ngIf="(data.percentage ?? data.percentageScore) !== null && (data.percentage ?? data.percentageScore) !== undefined; else noScore">
        <span class="fw-bold"
          [ngClass]="{
            'text-success': (data.percentage ?? data.percentageScore) >= 70,
            'text-warning': (data.percentage ?? data.percentageScore) >= 50 && (data.percentage ?? data.percentageScore) < 70,
            'text-danger':  (data.percentage ?? data.percentageScore) < 50
          }">
          {{ (data.percentage ?? data.percentageScore) | number:'1.0-1' }}%
        </span>
      </ng-container>
      <ng-template #noScore><span class="text-muted">—</span></ng-template>
    </ng-template>

    <ng-template #passedTpl let-data>
      <span [ngClass]="getPassedClass(data.passed)">
        <ng-container *ngIf="data.passed !== null; else pending">
          {{ data.passed ? ('TESTS.PASSED' | translate) : ('TESTS.FAILED' | translate) }}
        </ng-container>
        <ng-template #pending>—</ng-template>
      </span>
    </ng-template>

    <ng-template #startedTpl let-data>
      {{ data.startedAt ? (data.startedAt | date: 'yyyy-MM-dd HH:mm') : '—' }}
    </ng-template>

    <ng-template #antifraudTpl let-data>
      <div class="d-flex flex-column align-items-center gap-1">
        <span class="badge d-flex align-items-center gap-1"
          [ngClass]="(data.tabSwitchCount ?? 0) > 2 ? 'bg-warning text-dark' : 'bg-light text-dark border'">
          <i class="fas fa-exchange-alt"></i>
          {{ data.tabSwitchCount ?? 0 }}
          {{ 'CHEATING.TAB_SWITCH' | translate }}
        </span>
        <span class="badge d-flex align-items-center gap-1"
          [ngClass]="(data.fullscreenExitCount ?? 0) > 1 ? 'bg-danger' : 'bg-light text-dark border'">
          <i class="fas fa-expand"></i>
          {{ data.fullscreenExitCount ?? 0 }}
          {{ 'CHEATING.FULLSCREEN_EXIT' | translate }}
        </span>
      </div>
    </ng-template>

    <ng-template #actionsTpl let-data>
      <button class="btn btn-sm ct-icon-btn" (click)="viewSession(data)"
        [title]="'BUTTONS.VIEW' | translate">
        <i class="fas fa-eye"></i>
      </button>
      <ng-container *appCan="'tests.read.all'">
        <button class="btn btn-sm ct-icon-btn"
          (click)="openDiagnosticReport(data.studentId, data.subjectId)"
          [title]="'BUTTONS.OPEN_REPORT' | translate"
          *ngIf="data.status === 'completed'">
          <i class="fas fa-chart-column"></i>
        </button>
      </ng-container>
    </ng-template>
  </div>
</ng-template>
